import React, { useState, useEffect, useCallback } from 'react';
import './NoticiasList.css';
import { apiFetch } from '../../../services/dashboardApi';
import DashboardButton from './ui/DashboardButton';

function NoticiasList({ token, canEdit = false }) {
    const [noticias, setNoticias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingNoticia, setEditingNoticia] = useState(null);

    const fetchNoticias = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiFetch('http://localhost:8000/api/noticias/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setNoticias(data.results || data || []);
        } catch (err) {
            console.error('Error al cargar noticias:', err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchNoticias();
    }, [fetchNoticias]);

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar esta noticia?')) return;
        try {
            const response = await apiFetch(`http://localhost:8000/api/noticias/${id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Noticia eliminada');
                fetchNoticias();
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    if (showForm) {
        return (
            <NoticiaForm
                noticia={editingNoticia}
                token={token}
                onCancel={() => { setShowForm(false); setEditingNoticia(null); fetchNoticias(); }}
            />
        );
    }

    return (
        <div>
            <h3>Gestión de Noticias</h3>
            {canEdit && (
                <DashboardButton variant="success" onClick={() => { setEditingNoticia(null); setShowForm(true); }}>
                    Crear Nueva Noticia
                </DashboardButton>
            )}

            {loading && <p>Cargando noticias...</p>}

            {!loading && noticias.length === 0 && <p>No hay noticias publicadas.</p>}

            {!loading && Array.isArray(noticias) && noticias.map(noticia => (
                <div key={noticia.id} className="noticia-card">
                    <h4>{noticia.titulo}</h4>
                    <p><strong>Categoría:</strong> {noticia.categoria || 'N/A'}</p>
                    <p><strong>Fecha de Publicación:</strong> {noticia.fpublicacion}</p>
                    <p>{noticia.noticia?.substring(0, 200)}...</p>
                    {noticia.imagen && <img src={noticia.imagen} alt={noticia.titulo} className="noticia-image" />}
                    {canEdit && (
                        <div className="noticia-actions">
                            <DashboardButton onClick={() => { setEditingNoticia(noticia); setShowForm(true); }} variant="warning">
                                Editar
                            </DashboardButton>
                            <DashboardButton onClick={() => handleDelete(noticia.id)} variant="danger">
                                Eliminar
                            </DashboardButton>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function NoticiaForm({ noticia, token, onCancel }) {
    const [formData, setFormData] = useState({
        titulo: '',
        categoria: '',
        noticia: '',
        fpublicacion: new Date().toISOString().split('T')[0]
    });
    const [imagen, setImagen] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (noticia) {
            setFormData({
                titulo: noticia.titulo || '',
                categoria: noticia.categoria || '',
                noticia: noticia.noticia || '',
                fpublicacion: noticia.fpublicacion || new Date().toISOString().split('T')[0]
            });
        }
    }, [noticia]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setImagen(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const url = noticia
                ? `http://localhost:8000/api/noticias/${noticia.id}/`
                : 'http://localhost:8000/api/noticias/';
            const method = noticia ? 'PATCH' : 'POST';

            // Usar FormData para enviar archivos
            const data = new FormData();
            data.append('titulo', formData.titulo);
            data.append('categoria', formData.categoria);
            data.append('noticia', formData.noticia);
            data.append('fpublicacion', formData.fpublicacion);
            if (imagen) {
                data.append('imagen', imagen);
            }

            const response = await apiFetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`
                    // No incluir Content-Type, el navegador lo configurará automáticamente con boundary
                },
                body: data
            });

            if (response.ok) {
                alert(noticia ? 'Noticia actualizada' : 'Noticia creada');
                onCancel();
            } else {
                const responseData = await response.json();
                alert('Error: ' + JSON.stringify(responseData));
            }
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h3>{noticia ? 'Editar Noticia' : 'Crear Nueva Noticia'}</h3>
            <form onSubmit={handleSubmit} className="form-max-width">
                <div className="form-group">
                    <label>Título:*</label>
                    <input
                        type="text"
                        name="titulo"
                        value={formData.titulo}
                        onChange={handleChange}
                        required
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Categoría:</label>
                    <input
                        type="text"
                        name="categoria"
                        value={formData.categoria}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Contenido:*</label>
                    <textarea
                        name="noticia"
                        value={formData.noticia}
                        onChange={handleChange}
                        required
                        rows="10"
                        className="form-textarea"
                    />
                </div>

                <div className="form-group">
                    <label>Fecha de Publicación:</label>
                    <input
                        type="date"
                        name="fpublicacion"
                        value={formData.fpublicacion}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Imagen:</label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="form-input"
                    />
                    {noticia && noticia.imagen && (
                        <div className="image-preview">
                            <p>Imagen actual:</p>
                            <img src={noticia.imagen} alt="Preview" className="preview-img" />
                        </div>
                    )}
                    {imagen && (
                        <p className="new-image-message">Nueva imagen seleccionada: {imagen.name}</p>
                    )}
                </div>

                <div className="form-actions">
                    <DashboardButton type="submit" disabled={loading} variant="success">
                        {loading ? 'Guardando...' : (noticia ? 'Actualizar' : 'Crear')}
                    </DashboardButton>
                    <DashboardButton type="button" onClick={onCancel} variant="secondary">
                        Cancelar
                    </DashboardButton>
                </div>
            </form>
        </div>
    );
}

export default NoticiasList;
